/*
 * @Author: v-fmasoud@tableau.com
 * @Date: 2017-10-17 21:21:38
 * @Last Modified by: fmasoud@tableau.com
 * @Last Modified time: 2018-06-12 14:43:54
 */

import { UserGroups } from '../common/Constants';
import rolesWithExtraFields from '../components/forms/Data/roles-with-extra-fields.json';

// How the groups are modeled differ from the UI.
// so lets map them to something that makes sense.
function mapGroups(user, userModel) {
  if (user.authorization && user.authorization.groups) {
    const groups = user.authorization.groups;
    userModel.emailViewers = groups.find(group => group === UserGroups.emailViewers);
    userModel.permissionGranters = groups.find(group => group === UserGroups.permissionGranters);
    userModel.group = groups.find(
      group => group !== UserGroups.emailViewers && group !== UserGroups.permissionGranters,
    );
    userModel.group = !userModel.group ? UserGroups.None : userModel.group;
    userModel.memberOfGroups = groups.join('/');
  } else {
    userModel.group = '';
    userModel.emailViewers = '';
    userModel.permissionGranters = '';
  }
}
// Takes an auth0 user object and converts it to a UserModel
export const mapUser = (user) => {
  const userModel = {
    company: user.company,
    companySize: user.companySize,
    country: user.country,
    thoroughfare: user.thoroughfare,
    premise: user.premise,
    localityname: user.localityName,
    administrativearea: user.administrativeArea,
    dependent_localityname: user.dependentLocalityname,
    postalcode: user.postalCode,
    created: user.createdAt,
    department: user.department,
    email: user.email,
    emailViewers: null,
    firstName: user.firstName,
    fullName: `${user.firstName} ${user.lastName}`,
    group: null,
    industry: user.industry,
    jobRole: user.jobRole,
    title: user.title,
    lastLogin: user.lastLogin,
    lastName: user.lastName,
    legacyGroup: user.legacyGroup,
    memberOfGroups: null,
    permissionGranters: null,
    phone: user.phone ? user.phone : '',
    status: user.status,
    uid: user.uid,
    updated: user.updatedAt,
    userAlias: user.userAlias,
    userId: user.userId,
    verified: user.emailVerified,
    language: user.language,
    emailHistory: user.emailHistory,
    loginHistory: user.clientLastLoginInfo,
  };
  mapGroups(user, userModel);
  return userModel;
};

// Takes an array of auth0 users and returns a list of UserModels
export const mapUsers = users => users.map(user => mapUser(user));

const addressElements = [
  'thoroughfare',
  'premise',
  'localityname',
  'administrativeArea',
  'postalcode',
  'dependentLocalityName',
];

// Helper function to compare and return field value if changed
const getChangedField = (inputValue, modelValue) => (inputValue !== modelValue ? inputValue : null);

const mapCommonInfo = (userModel, inputs) => {
  if (inputs.country === '') {
    addressElements.forEach((key) => {
      inputs[key] = '';
    });
  } else {
    addressElements.forEach((key) => {
      inputs[key] = inputs[key] === undefined ? '' : inputs[key];
    });
  }
  const result = {
    administrativeArea: getChangedField(inputs.administrativearea, userModel.administrativearea),
    company: getChangedField(inputs.company, userModel.company),
    companySize: getChangedField(inputs.companySize, userModel.companySize),
    country: getChangedField(inputs.country, userModel.country),
    department: getChangedField(inputs.department, userModel.department),
    firstName: getChangedField(inputs.firstName, userModel.firstName),
    industry: getChangedField(inputs.industry, userModel.industry),
    jobRole: getChangedField(inputs.jobRole, userModel.jobRole),
    language: inputs.language,
    lastName: getChangedField(inputs.lastName, userModel.lastName),
    localityName: getChangedField(inputs.localityname, userModel.localityname),
    phone: getChangedField(inputs.phone, userModel.phone),
    postalCode: getChangedField(inputs.postalcode, userModel.postalcode),
    premise: getChangedField(inputs.premise, userModel.premise),
    dependentLocalityname: getChangedField(inputs.dependent_localityname, userModel.dependent_localityname),
    thoroughfare: getChangedField(inputs.thoroughfare, userModel.thoroughfare),
    title: getChangedField(inputs.title, userModel.title),
    userAlias: getChangedField(inputs.userAlias, userModel.userAlias),
    emailHistory: getChangedField(inputs.emailHistory, userModel.emailHistory),
  };

  return result;
};

// Takes a userModel and input values from a form and returns a user object in the auth0 format.
export const mapInputsFromUserEdit = (userModel, inputs) => {
  const groups = [];
  let groupsChanged = false;
  if (inputs.group !== null && userModel.group !== inputs.group) {
    groupsChanged = true;
  }
  if (inputs.emailViewers !== null && userModel.emailViewers !== inputs.emailViewers) {
    groupsChanged = true;
  }
  if (inputs.permissionGranters !== null && userModel.permissionGranters !== inputs.permissionGranters) {
    groupsChanged = true;
  }

  if (groupsChanged) {
    groups.push(inputs.group);
    if (inputs.emailViewers && inputs.emailViewers !== '') {
      groups.push(inputs.emailViewers);
    }
    if (inputs.permissionGranters && inputs.permissionGranters !== '') {
      groups.push(inputs.permissionGranters);
    }
  }

  const userInfo = mapCommonInfo(userModel, inputs);
  userInfo.status = inputs.status !== userModel.status ? inputs.status : null;

  const user = {
    userId: userModel.userId,
    uid: userModel.uid,
    ...userInfo,
  };

  if (groups.length > 0) {
    user.authorization = { groups };
  }
  window.inputs = inputs;
  window.user = user;
  return user;
};

// Takes a userModel and input values from a form and returns a user object in the auth0 format.
export const mapInputsFromMyProfileEdit = (userModel, inputs) => {
  const userInfo = mapCommonInfo(userModel, inputs);
  const user = {
    userId: userModel.userId,
    uid: userModel.uid,
    ...userInfo,
  };
  return user;
};

// Starts with mapInputsFromMyProfileEdit and decorates with publicData.
export const mapInputsFromAdditionalInfoEdit = (userModel, inputs) => {
  // <select> element uses strings so we need to convert to an integer for the API.
  const parsedCompanySize = Number(inputs.companySize);
  const normalizedCompanySize = Number.isInteger(parsedCompanySize) ? parsedCompanySize : 0;

  // Map value of title or role to the title property, depending on chosen role.
  const titleValue = rolesWithExtraFields[inputs.role] ? inputs.title : inputs.role;

  const mappedInputs = { ...inputs, companySize: normalizedCompanySize, title: titleValue };
  const myProfileEditUserData = mapInputsFromMyProfileEdit(userModel, mappedInputs);

  // This uses the Checkbox component, where possible values are the value prop
  // (a string) if checked or '' if unchecked. This converts to Boolean for the API.
  const marketingEmailOptinBoolean = Boolean(inputs.marketingEmailOptIn);

  const publicData = {
    codeOfConductAcceptance: inputs.codeOfConductAcceptance,
    marketingEmailOptIn: marketingEmailOptinBoolean,
  };
  const user = {
    publicData,
    ...myProfileEditUserData,
  };
  return user;
};
